import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ASSETS, LINKS } from "../lib/assets";

const LangSwitch = ({ className = "" }) => {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center border border-navy/15 ${className}`} role="group" aria-label="Language">
      {[
        { code: "en", label: "EN" },
        { code: "ta", label: "தமிழ்" },
      ].map(({ code, label }) => (
        <button
          key={code}
          data-testid={`lang-switch-${code}`}
          onClick={() => setLang(code)}
          className={`px-3 py-1.5 text-xs font-medium tracking-wide transition-colors duration-300 ${
            lang === code ? "bg-navy text-gold" : "text-navy hover:bg-navy/5"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home, id: "nav-home" },
    { to: "/about", label: t.nav.about, id: "nav-about" },
    { to: "/academics", label: t.nav.academics, id: "nav-academics" },
    { to: "/contact", label: t.nav.contact, id: "nav-contact" },
  ];

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled ? "bg-ivory/95 shadow-[0_1px_0_0_rgba(197,160,89,0.35)] backdrop-blur-sm" : "bg-ivory/0"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3">
          <img src={ASSETS.logo} alt="SMHSS crest" className="h-12 w-auto sm:h-14" />
          <span className="hidden flex-col leading-tight sm:flex">
            <span className="font-heading text-lg font-semibold tracking-wide text-navy">
              {t.common.schoolShort}
            </span>
            <span className="text-[10px] uppercase tracking-[0.3em] text-gold">{t.common.motto}</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" aria-label="Primary">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.id}
              className={({ isActive }) =>
                `group relative py-1 text-sm font-medium transition-colors duration-300 ${
                  isActive ? "text-navy" : "text-ink/70 hover:text-navy"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-0.5 left-0 h-px bg-gold transition-[width] duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LangSwitch />
          <a
            href={LINKS.tel}
            data-testid="nav-call-btn"
            className="group inline-flex items-center gap-2 bg-navy px-5 py-2.5 text-sm font-medium text-ivory transition-colors duration-300 hover:bg-navy-soft"
          >
            <Phone size={14} className="text-gold" />
            {t.common.call}
          </a>
        </div>

        <div className="flex items-center gap-3 lg:hidden">
          <LangSwitch />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="p-2 text-navy"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-gold/30 bg-ivory lg:hidden"
          >
            <div className="flex flex-col px-6 py-4">
              {links.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.id}`}
                  className={({ isActive }) =>
                    `border-b border-navy/5 py-4 font-heading text-2xl ${
                      isActive ? "text-gold" : "text-navy"
                    }`
                  }
                  style={{ transitionDelay: `${i * 40}ms` }}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
